import { QuantityField } from './calculate-article-quantities-action';
import { SizingStore } from './sizing-store';
import { SizingGroup } from '../../models/sizing-group';
import { Article } from '../../models/sizing-data';
import { sendSizingAppliedEventForGroups } from '../../lib/fragment-helper';
import { Distribution } from '../../models/sizing';
import isEqual from 'fast-deep-equal';

type ReferenceIdSizingMap = { [referenceId: string]: Distribution | undefined };

export class CalculateQuantitiesAction {
  async calculateQuantities(
    this: SizingStore,
    initialCalculation?: boolean,
    group?: SizingGroup,
    articleReferenceIds?: string[],
    fieldToCalculate?: QuantityField,
    skipBhosNotification?: boolean
  ): Promise<Article[] | undefined> {
    const optimizePromises: Promise<void>[] = [];
    const previousSizings: ReferenceIdSizingMap = {};
    const previousBlockOrderSizings: ReferenceIdSizingMap = {};
    let articlesWithChangedSizing: Article[] | undefined = [];

    const groupsToCalculate = group ? [group] : this.sizingGroups;
    for (const group of groupsToCalculate) {
      // If sizechartOverride enabled, dont bother recalculating
      let articles = group.articles.filter((article) => !article.sizeChartOverride);
      if (articleReferenceIds?.length) {
        articles = articles.filter((article) => articleReferenceIds.includes(article.referenceId));
      }

      for (const article of articles) {
        if (article.editedQuantities) {
          article.sizing = article.editedQuantities;
          continue;
        }
        // Keep previous sizing to compare later if something changed
        previousSizings[article.referenceId] = article.sizing && { ...article.sizing };
        previousBlockOrderSizings[article.referenceId] = article.blockOrderSizing
          ? { ...article.blockOrderSizing }
          : {};
        // Apply percentage distribution to config quantity to calculate sizing (simple quantities)
        this.calculateArticleQuantities(
          article,
          group?.selectedSizeKey?.distribution,
          initialCalculation,
          fieldToCalculate
        );
      }
      if (this.enableBox) {
        for (const article of articles.filter((article) => !article.boxing?.editedBoxQuantities)) {
          optimizePromises.push(this.optimizeBoxQuantities(group, article));
        }
      }
    }

    // All box restrictions must be done before sending sizingAppliedEvent
    await Promise.all(optimizePromises);

    articlesWithChangedSizing = this.notifyAboutChangedArticles(
      previousSizings,
      previousBlockOrderSizings,
      skipBhosNotification
    );
    return articlesWithChangedSizing;
  }

  private notifyAboutChangedArticles(
    this: SizingStore,
    previousSizings: ReferenceIdSizingMap,
    previousBlockOrderSizings: ReferenceIdSizingMap,
    skipBhosNotification?: boolean
  ) {
    const changedArticles = this.findSizingChanges(previousSizings, 'sizing');
    const changedArticleBlockOrders = this.findSizingChanges(previousBlockOrderSizings, 'blockOrderSizing');
    // If sizing quantities changed we have to tell buying home about that
    if (changedArticles.length > 0) {
      if (!skipBhosNotification) sendSizingAppliedEventForGroups(this.sizingGroups, changedArticles);
      return changedArticles;
    } else if (changedArticleBlockOrders.length > 0) {
      if (!skipBhosNotification) sendSizingAppliedEventForGroups(this.sizingGroups, changedArticleBlockOrders);
      return changedArticleBlockOrders;
    }
  }

  private findSizingChanges(
    this: SizingStore,
    sizings: ReferenceIdSizingMap,
    sizingType: 'sizing' | 'blockOrderSizing'
  ): Article[] {
    const articlesByReferenceId = this.articlesByReferenceId();
    return Object.entries(sizings).reduce((list, [referenceId, previousSizing]) => {
      const article = articlesByReferenceId[referenceId];
      if (!article) throw new Error(`Article with referenceId '${referenceId}' not found!`);
      if (article[sizingType] && previousSizing && !isEqual(article[sizingType], previousSizing)) {
        list.push(article);
      }
      return list;
    }, [] as Article[]);
  }
}
